import { Add, ArrowBack, InfoOutlined } from "@mui/icons-material";
import {
  Alert,
  Box,
  Card,
  Icon,
  IconButton,
  Button as MuiButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import React, { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Button from "../../components/Button";
import { UserContext } from "../../context/UserContext";
import { convertDate, convertToINR } from "../../helpers/Utils";
import {
  deleteInvoice,
  getInvoiceListByPO,
  updateInvoice,
  uploadInvoiceImage,
} from "../../services/invoiceService";
import { getPOByCode } from "../../services/poService";
import { latestInvoicePayment } from "./utils/payments";
import OrderStatusBadge from "./components/OrderStatusBadge";
import LoaderV2 from "./components/LoaderV2";
import RejectDialog from "./components/RejectDialog";
import UploadFile from "./components/UploadFile";
import { ACTION_TYPE, actions } from "./constants/actions";
import { PERMISSIONS } from "./constants/permissions";
import { ApprovalStatus } from "./constants/statusMapping";
import { TOKENS } from "./constants/figmaTokens";
import { useToast } from "./hooks/useToast";
import { getApprovalPayload, getRejectPayload } from "./utils/approveReject";
import { useApprovalMapping } from "./hooks/useApprovalMapping";
import { deriveCreditNoteBannerState } from "./utils/creditNote";
import { summariseInvoiceTableData } from "./utils/invoice";
import { usePermissions } from "../../context/PermissionsContext";
import { BOUNCER_PERMISSIONS } from "../../constants/bouncerPermissions";
import { WithPermission } from "./components/WithPermission";
import { getBackendErrorMessage } from "../../helpers/errorUtils";
import CreditNoteReview from "./CreditNoteReview";

const initialRejectionState = {
  show: false,
  data: null,
};

// Column header style shared with PO items table
const ITEM_COL_HEADER_SX = {
  fontSize: 12,
  fontWeight: 500,
  textTransform: "uppercase",
  color: TOKENS.onSurfaceSecondary,
};

function SectionHeading({ children }) {
  return (
    <Typography
      sx={{
        fontSize: 16,
        fontWeight: 700,
        lineHeight: "24px",
        color: TOKENS.onSurfaceDefault,
        marginBottom: 2,
      }}
    >
      {children}
    </Typography>
  );
}

function AddressBlock({ heading, bodyAddress, pin, gst }) {
  if (!bodyAddress) {
    return (
      <Box>
        <SectionHeading>{heading}</SectionHeading>
        <Typography sx={{ fontSize: 14, lineHeight: "20px", color: TOKENS.onSurfaceSecondary }}>
          -
        </Typography>
      </Box>
    );
  }
  const lines = [
    bodyAddress.display_name,
    bodyAddress.line_one,
    bodyAddress.line_two,
    bodyAddress.line_three,
    bodyAddress.city,
    bodyAddress.state,
  ].filter(Boolean);
  return (
    <Box>
      <SectionHeading>{heading}</SectionHeading>
      {lines.map((line, idx) => (
        <Typography key={idx} sx={{ fontSize: 14, lineHeight: "20px", color: TOKENS.onSurfaceDefault }}>
          {line}
        </Typography>
      ))}
      <Typography sx={{ fontSize: 14, lineHeight: "20px", color: TOKENS.onSurfaceDefault }}>
        Pin: {pin || "NA"}
      </Typography>
      <Typography sx={{ fontSize: 14, lineHeight: "20px", color: TOKENS.onSurfaceDefault }}>
        GSTIN: {gst || "NA"}
      </Typography>
    </Box>
  );
}

const ActionButton = (props) => {
  const { primary, ...restProps } = props;
  return (
    <MuiButton
      sx={{
        textTransform: "none",
        color: primary
          ? TOKENS.primaryDefault
          : TOKENS.onSurfaceSecondary,
        whiteSpace: "nowrap",
      }}
      {...restProps}
    >
      {props.children}
    </MuiButton>
  );
};

const UploadView = ({ data, onUpload }) => {
  const { image } = data;
  if (!image || !image.pre_signed_url)
    return <UploadFile label={"Upload invoice"} onUpload={onUpload} />;
  return (
    <Box padding="6px">
      <a href={image.pre_signed_url} target="_blank" rel="noreferrer">
        {image.file_name || "Invoice Link"}
      </a>
    </Box>
  );
};

const InfoTooltip = (props) => {
  if (!props.title) return null;
  return (
    <Tooltip title={props.title}>
      <InfoOutlined fontSize="small" />
    </Tooltip>
  );
};

const AddNewInvoiceButton = (props) => (
  <WithPermission permission={BOUNCER_PERMISSIONS.INVOICE_UPLOAD}>
    <Button type="primary" fillStyle="filled" onClick={props.onClick}>
      <Stack direction="row" gap={1} alignItems="center">
        Add new
        <Icon>
          <Add />
        </Icon>
      </Stack>
    </Button>
  </WithPermission>
);

const InvoiceDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useContext(UserContext);
  const { showToast } = useToast();

  const [showRejectDialog, setShowRejectDialog] = useState(initialRejectionState);
  const resetRejection = () => setShowRejectDialog(initialRejectionState);
  const [selectedCreditNote, setSelectedCreditNote] = useState(null);

  const { po_code } = params || {};

  const showErrorToast = ({ message, error }) =>
    showToast({ type: "error", message: message + getBackendErrorMessage(error) });

  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(BOUNCER_PERMISSIONS.INVOICE_EDIT);
  const canDelete = hasPermission(BOUNCER_PERMISSIONS.INVOICE_DELETE);

  const { approvalMapping, canVmApproval, canL1Approval, canL2Approval } = useApprovalMapping();

  // Invoice list
  const { isLoading, isError, data, error, isFetching } = useQuery(
    ["invoiceList", po_code],
    () => getInvoiceListByPO({ po_code }),
    {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    }
  );

  // FINS PO rich data — gracefully absent for IMS POs
  const { data: po } = useQuery(
    ["po-by-code", po_code],
    () => getPOByCode(po_code),
    { retry: false, refetchOnWindowFocus: false, enabled: !!po_code }
  );

  const { isLoading: isUploading, mutate } = useMutation(uploadInvoiceImage, {
    onSuccess: () => {
      showToast({ type: "success", message: "Uploaded Invoice successfully" });
      queryClient.invalidateQueries({ queryKey: ["invoiceList"] });
    },
    onError: (error) => {
      showErrorToast({ error, message: "Error uploading invoice image" });
    },
  });

  const { isLoading: isDeleting, mutate: deleteInvoiceMutate } = useMutation(
    deleteInvoice,
    {
      onSuccess: (data) => {
        showToast({
          type: "success",
          message: `Invoice ${data.invoice_no || ""} deleted successfully`,
        });
        queryClient.invalidateQueries({ queryKey: ["invoiceList"] });
      },
      onError: (error) => {
        showErrorToast({ error, message: "Error Deleting invoice " });
      },
    }
  );

  const { isLoading: isUpdating, mutate: updateInvoiceMutate } = useMutation(
    updateInvoice,
    {
      onSuccess: () => {
        showToast({ type: "success", message: "Updated invoice successfully" });
        resetRejection();
        queryClient.invalidateQueries({ queryKey: ["invoiceList"] });
      },
      onError: (error) => {
        showErrorToast({ error, message: "Error updating invoice " });
      },
    }
  );

  // Action Handlers
  const handleUploadFile = (invoiceNum, file) => {
    let formData = new FormData();
    formData.append("image", file);
    mutate({ payload: formData, invoiceNo: invoiceNum, poCode: po_code });
  };

  const handleDelete = (invoiceNo) => {
    deleteInvoiceMutate({ invoiceNo, empId: user?.email, po_code });
  };

  const handleEdit = () => {
    let milestoneArr = data?.po_list?.[0]?.milestone?.split("-");
    let source = data?.po_list?.[0]?.source;
    if (source === "FINS" && milestoneArr?.length === data?.po_list?.[0]?.invoices?.length) {
      showToast({
        type: "error",
        message: "Cannot add more invoices than milestones",
      });
      return;
    }
    navigate(`/dashboard/invoice-management/${encodeURIComponent(po_code)}/edit`, {
      state: {
        milestone: data?.po_list?.[0]?.milestone,
        invoices: data?.po_list?.[0]?.invoices,
        po_code,
        amount: data?.po_list?.[0]?.order_value,
        source: data?.po_list?.[0]?.source,
        edit: false,
        from: "details",
      },
    });
  };

  const isInvoiceCopyPresent = (data) =>
    data.invoice_image?.pre_signed_url?.length > 1;

  const showNonEditableToast = () => {
    showToast({
      type: "error",
      message: "Cannot be edited/deleted after completion",
    });
  };

  const nonEditableAfterCompletion = (data) =>
    data.invoice_status?.label === "completed";

  const handleActionClick = (action, data, poData) => {
    switch (action) {
      case ACTION_TYPE.reject: {
        const { permission, key, message } = getRejectPayload(data);
        if (permission === PERMISSIONS.deny || !approvalMapping(permission)) {
          showToast({ type: "error", message: message || "Permission denied" });
          return;
        }
        if (!isInvoiceCopyPresent(data)) {
          showToast({ type: "error", message: "Action denied. No invoice copy found." });
          return;
        }
        setShowRejectDialog({ show: true, data: { ...data, action_key: key } });
        break;
      }
      case ACTION_TYPE.approve: {
        const { permission, key, message } = getApprovalPayload(data);
        if (permission === PERMISSIONS.deny || !approvalMapping(permission) || !key) {
          showToast({ type: "error", message: message || "Permission denied" });
          return;
        }
        if (!isInvoiceCopyPresent(data)) {
          showToast({ type: "error", message: "Action denied. No invoice copy found." });
          return;
        }
        const actionField = `${key}_approval_status`;
        updateInvoiceMutate({
          payload: { invoice_no: data.invoice_no, [actionField]: "approved", po_code },
        });
        break;
      }
      case ACTION_TYPE.edit: {
        if (nonEditableAfterCompletion(data)) {
          showNonEditableToast();
          return;
        }
        if (data.l1_approval_status === "approved" && data.l2_approval_status !== "rejected") {
          showToast({ type: "error", message: "Permission denied" });
          return;
        }
        navigate(`/dashboard/invoice-management/${encodeURIComponent(po_code)}/edit`, {
          state: {
            ...data,
            edit: true,
            from: "details",
            milestone: poData?.milestone,
            invoices: poData?.invoices,
            source: poData?.source,
            amount: poData?.order_value,
          },
        });
        return;
      }
      case ACTION_TYPE.delete: {
        if (nonEditableAfterCompletion(data)) {
          showNonEditableToast();
          return;
        }
        if (data.l1_approval_status === "approved" && data.l2_approval_status !== "rejected") {
          showToast({ type: "error", message: "Permission denied" });
          return;
        }
        handleDelete(data?.invoice_no);
        return;
      }
      default:
        return;
    }
  };

  const getRejectReason = (invoice) => {
    const {
      vm_reject_reason,
      l1_reject_reason,
      l2_reject_reason,
      system_rejection_reason,
      invoice_status,
    } = invoice;
    if (invoice_status && invoice_status.state === "rejected") {
      return (
        system_rejection_reason ||
        vm_reject_reason ||
        l1_reject_reason ||
        l2_reject_reason ||
        "Invoice value is greater than PO value"
      );
    }
    return null;
  };

  if (!po_code) return <div>No PO Code Found</div>;
  if (isLoading || isUploading || isFetching || isDeleting || isUpdating)
    return <LoaderV2 />;
  if (isError) return <div>{error.message}</div>;

  const poData = data?.po_list?.[0];
  const invoices = poData?.invoices ?? [];
  const { totalInvoicedValue, approvedCount, approvedInvoiceAmount } =
    summariseInvoiceTableData(invoices);
  // Payments come from the PO details response (ENG-27224 union); see InvoiceDetailsV2.
  const payments = poData?.payments ?? [];
  const totalPaid = poData?.total_amount_paid ?? 0;
  const items = po?.po_items ?? [];

  const isPaymentDateExist = (invoice) => latestInvoicePayment(payments, invoice.invoice_no);

  const cnBanner = deriveCreditNoteBannerState(invoices);

  const actionItems = actions
    .filter((action) => {
      if (action.id === ACTION_TYPE.edit) return canEdit;
      if (action.id === ACTION_TYPE.delete) return canDelete;
      if (action.id === ACTION_TYPE.approve || action.id === ACTION_TYPE.reject)
        return canVmApproval || canL1Approval || canL2Approval;
      return true;
    })
    .map((item) => ({
      ...item,
      onClick: (invoiceData) => handleActionClick(item.id, invoiceData, poData),
    }));

  const subHeader = (
    <Box
      sx={{
        bgcolor: TOKENS.surfaceDefault,
        borderBottom: `1px solid ${TOKENS.surfaceVariant}`,
        display: "flex",
        alignItems: "center",
        height: 60,
        pl: "8px",
        pr: "24px",
        flexShrink: 0,
      }}
    >
      <Box
        data-testid="back-button"
        onClick={() => navigate(-1)}
        sx={{
          width: 36,
          height: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "4px",
          cursor: "pointer",
          "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
        }}
      >
        <ArrowBack sx={{ fontSize: 24, color: TOKENS.onSurfaceSecondary }} />
      </Box>
      <Box display="flex" flexDirection="column" alignItems="flex-start" pb="4px">
        <Typography sx={{ fontSize: 12, lineHeight: "16px", color: TOKENS.onSurfaceSecondary }}>
          Orders/
        </Typography>
        <Typography
          sx={{ fontSize: 20, fontWeight: 600, lineHeight: "24px", color: TOKENS.onSurfaceDefault }}
        >
          {po_code}
        </Typography>
      </Box>
      <Box sx={{ flexGrow: 1 }} />
      <Box display="flex" gap="24px" alignItems="center">
        <Box display="flex" flexDirection="column" alignItems="flex-end">
          <Typography sx={{ fontSize: 10, lineHeight: "12px", color: TOKENS.onSurfaceSecondary }}>
            Order Value
          </Typography>
          <Typography
            sx={{ fontSize: 16, fontWeight: 700, lineHeight: "24px", color: TOKENS.onSurfaceDefault }}
          >
            {convertToINR(poData?.order_value)}
          </Typography>
        </Box>
        <Box display="flex" flexDirection="column" alignItems="flex-end">
          <Typography sx={{ fontSize: 10, lineHeight: "12px", color: TOKENS.onSurfaceSecondary }}>
            Invoice Value
          </Typography>
          <Typography
            sx={{ fontSize: 16, fontWeight: 700, lineHeight: "24px", color: TOKENS.onSurfaceDefault }}
          >
            {convertToINR(totalInvoicedValue)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: TOKENS.surfaceBg, minHeight: "100vh" }}>
      <RejectDialog
        open={showRejectDialog.show}
        data={showRejectDialog.data}
        onClose={() => setShowRejectDialog({ show: false, data: null })}
        onReject={(data) => {
          if (data.action_key) {
            const action = `${data.action_key}_approval_status`;
            const reason = `${data.action_key}_reject_reason`;
            updateInvoiceMutate({
              payload: {
                [action]: "rejected",
                [reason]: data.reason || "-",
                po_code,
                invoice_no: data.invoice_no,
              },
            });
          }
        }}
      />

      {subHeader}

      <Box sx={{ padding: "24px", display: "flex", flexDirection: "column", gap: "24px" }}>
        {/* Credit note banners */}
        {cnBanner.mode === "pending" && (
          <Alert severity="warning">Awaiting approval</Alert>
        )}
        {cnBanner.mode === "rejected" && (
          <Alert severity="error">
            Credit note{cnBanner.numbers.length > 1 ? "s" : ""}{" "}
            {cnBanner.numbers.join(", ")} rejected. {cnBanner.reasonText}. Please re-upload.
          </Alert>
        )}

        {/* Approved invoice + Payment summary */}
        <Card sx={{ bgcolor: TOKENS.surfaceDefault, borderRadius: "8px", p: "24px", boxShadow: "none" }}>
          <Box display="flex" gap="48px">
            <Box flex={1}>
              <SectionHeading>Approved invoice</SectionHeading>
              <Typography sx={{ fontSize: 14, lineHeight: "20px" }}>
                {approvedCount > 0
                  ? `${convertToINR(approvedInvoiceAmount)} (${approvedCount} Invoice${approvedCount > 1 ? "s" : ""})`
                  : "No invoice"}
              </Typography>
            </Box>
            <Box flex={1}>
              <SectionHeading>Payment details</SectionHeading>
              <Typography sx={{ fontSize: 14, lineHeight: "20px" }}>
                {payments.length > 0
                  ? `${convertToINR(totalPaid)} (${payments.length} Payment${payments.length > 1 ? "s" : ""})`
                  : "No payments made"}
              </Typography>
            </Box>
          </Box>
        </Card>

        {/* Supplier + Delivery addresses — FINS POs only */}
        {po && (
          <Card sx={{ bgcolor: TOKENS.surfaceDefault, borderRadius: "8px", p: "24px", boxShadow: "none" }}>
            <Box display="flex" gap="48px">
              <Box flex={1}>
                <AddressBlock
                  heading="Supplier Address"
                  bodyAddress={po.vendor_details?.vendor_billing_address}
                  pin={po.vendor_details?.vendor_billing_address?.post_code}
                  gst={po.vendor_details?.gst_tin}
                />
              </Box>
              <Box flex={1}>
                <AddressBlock
                  heading="Delivery Address"
                  bodyAddress={po.shipping_address}
                  pin={po.billing_address?.post_code}
                  gst={po.billing_address?.tax_id}
                />
              </Box>
            </Box>
          </Card>
        )}

        {/* Invoice Details table */}
        <Card sx={{ bgcolor: TOKENS.surfaceDefault, borderRadius: "8px", boxShadow: "none", p: "24px" }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: "24px" }}>
                Invoice Details
              </Typography>
              <Typography sx={{ fontSize: 14, lineHeight: "20px", color: TOKENS.onSurfaceSecondary }}>
                Total Invoiced Value:{" "}
                <Box component="span" sx={{ color: TOKENS.onSurfaceDefault, fontWeight: 500 }}>
                  {convertToINR(totalInvoicedValue)} of {convertToINR(poData?.order_value || 0)}
                </Box>
              </Typography>
            </Box>
            <AddNewInvoiceButton onClick={handleEdit} />
          </Box>

          {invoices.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={2}>
              <img
                src={require("./assets/done.png")}
                height={80}
                width={80}
                alt="no invoices"
              />
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: TOKENS.onSurfaceDefault }}>
                No invoices for this PO
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={ITEM_COL_HEADER_SX}>Invoice Number</TableCell>
                    <TableCell sx={ITEM_COL_HEADER_SX}>Vendor Name</TableCell>
                    <TableCell sx={ITEM_COL_HEADER_SX}>Invoice Date</TableCell>
                    <TableCell sx={ITEM_COL_HEADER_SX}>Invoice Value</TableCell>
                    <TableCell sx={ITEM_COL_HEADER_SX}>Amount Paid</TableCell>
                    <TableCell sx={ITEM_COL_HEADER_SX}>Addl. Charges</TableCell>
                    <TableCell sx={ITEM_COL_HEADER_SX}>IRN</TableCell>
                    <TableCell sx={ITEM_COL_HEADER_SX}>GRN ID</TableCell>
                    <TableCell sx={ITEM_COL_HEADER_SX}>GRN Value</TableCell>
                    <TableCell sx={ITEM_COL_HEADER_SX}>GRN Date</TableCell>
                    <TableCell sx={ITEM_COL_HEADER_SX}>Credit Note</TableCell>
                    <TableCell sx={ITEM_COL_HEADER_SX}>Invoice</TableCell>
                    <TableCell sx={ITEM_COL_HEADER_SX}>Status</TableCell>
                    <TableCell sx={ITEM_COL_HEADER_SX}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((invoice) => {
                    const paymentEntry = isPaymentDateExist(invoice);
                    return (
                      <TableRow key={invoice.id}>
                        <TableCell>
                          <Stack direction="row" gap={1} alignItems="center">
                            {invoice.invoice_no}
                            <InfoTooltip title={getRejectReason(invoice)} />
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>{invoice.vendor_name}</TableCell>
                        <TableCell>{convertDate(invoice.invoice_date)}</TableCell>
                        <TableCell>{convertToINR(invoice.invoice_value)}</TableCell>
                        <TableCell>
                          {convertToINR(invoice.amount_paid)}
                          {paymentEntry?.payment_date && (
                            <Typography sx={{ fontSize: 10 }}>
                              [{convertDate(paymentEntry.payment_date)}]
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{convertToINR(invoice.additional_charges)}</TableCell>
                        <TableCell>{invoice.irn_invoice ? "Yes" : "No"}</TableCell>
                        <TableCell>{invoice.grn_id || "N/A"}</TableCell>
                        <TableCell>{convertToINR(invoice.grn_value)}</TableCell>
                        <TableCell>{convertDate(invoice.grn_date)}</TableCell>
                        <TableCell>
                          {invoice.credit_notes?.[0] ? (
                            <MuiButton
                              disableRipple
                              onClick={() => setSelectedCreditNote(invoice.credit_notes[0])}
                              variant="text"
                              style={{
                                textDecoration: "underline",
                                color: "black",
                                padding: 0,
                                minWidth: 0,
                              }}
                            >
                              {invoice.credit_notes[0].number}
                            </MuiButton>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          <UploadView
                            data={{
                              image: invoice.invoice_image,
                              invoiceNum: invoice.invoice_no,
                            }}
                            onUpload={(file) => {
                              file && handleUploadFile(invoice.invoice_no, file);
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <OrderStatusBadge
                            type={ApprovalStatus[invoice.invoice_status?.state]?.code}
                            label={invoice.invoice_status?.label ?? "-"}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row">
                            {actionItems.map((action) => {
                              const approveReject =
                                action.id === ACTION_TYPE.approve
                                  ? getApprovalPayload(invoice).permission
                                  : getRejectPayload(invoice).permission;
                              const actionPermitted = approvalMapping(approveReject);
                              return action.label ? (
                                actionPermitted ? (
                                  <ActionButton
                                    key={action.id}
                                    startIcon={action.icon}
                                    primary={action.primary}
                                    onClick={() => action.onClick(invoice)}
                                  >
                                    {action.label}
                                  </ActionButton>
                                ) : null
                              ) : (
                                <IconButton
                                  size="small"
                                  key={action.id}
                                  onClick={() => action.onClick(invoice)}
                                >
                                  {action.icon}
                                </IconButton>
                              );
                            })}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        {/* PO Items table — FINS POs only */}
        {po && (
          <Box>
            <Box display="flex" alignItems="center" gap="8px" mb={2}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: "24px" }}>
                PO Items
              </Typography>
              <Typography sx={{ fontSize: 12, lineHeight: "16px", color: TOKENS.onSurfaceSecondary }}>
                ({items.length})
              </Typography>
            </Box>
            <Card sx={{ borderRadius: "8px", boxShadow: "none", overflow: "hidden" }}>
              {items.length === 0 ? (
                <Box sx={{ p: "24px", textAlign: "center" }}>
                  <Typography sx={{ fontSize: 14, color: TOKENS.onSurfaceSecondary }}>
                    No items on this PO.
                  </Typography>
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 560 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={ITEM_COL_HEADER_SX}>SO Item Name</TableCell>
                        <TableCell align="right" sx={ITEM_COL_HEADER_SX}>
                          Total Amount with tax
                        </TableCell>
                        <TableCell sx={ITEM_COL_HEADER_SX}>Description</TableCell>
                        <TableCell sx={ITEM_COL_HEADER_SX}>Specification</TableCell>
                        <TableCell sx={ITEM_COL_HEADER_SX}>HSN/SAC Code (GST)</TableCell>
                        <TableCell align="right" sx={ITEM_COL_HEADER_SX}>
                          Unit Cost Price
                        </TableCell>
                        <TableCell align="right" sx={ITEM_COL_HEADER_SX}>
                          QTY
                        </TableCell>
                        <TableCell sx={ITEM_COL_HEADER_SX}>UOM</TableCell>
                        <TableCell align="right" sx={ITEM_COL_HEADER_SX}>
                          Total Amount
                        </TableCell>
                        <TableCell align="right" sx={ITEM_COL_HEADER_SX}>
                          CGST
                        </TableCell>
                        <TableCell align="right" sx={ITEM_COL_HEADER_SX}>
                          SGST
                        </TableCell>
                        <TableCell align="right" sx={ITEM_COL_HEADER_SX}>
                          IGST
                        </TableCell>
                        <TableCell sx={ITEM_COL_HEADER_SX}>PO Remarks</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((it, idx) => {
                        const tax = it.tax || {};
                        const amountWithTax =
                          it.amount != null ? it.amount + (tax.total || 0) : null;
                        const intra = tax.strategy === "intrastate";
                        const inter = tax.strategy === "interstate";
                        return (
                          <TableRow key={it.id || idx}>
                            <TableCell sx={{ fontSize: 14, lineHeight: "20px" }}>
                              {it.name || "-"}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 14, fontWeight: 500 }}>
                              {amountWithTax != null ? convertToINR(amountWithTax) : "-"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 14, maxWidth: 240 }}>
                              {it.description || "-"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 14, maxWidth: 200 }}>
                              {it.specification || "-"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 14 }}>{tax.hsn || "-"}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 14 }}>
                              {it.unit_cost_price != null ? convertToINR(it.unit_cost_price) : "-"}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 14 }}>
                              {it.quantity ?? "-"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 14 }}>{it.uom || "-"}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 14 }}>
                              {it.pre_tax_amount != null ? convertToINR(it.pre_tax_amount) : "-"}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 14 }}>
                              {intra
                                ? `${convertToINR(tax.cgst_amount || 0)} (${tax.cgst != null ? tax.cgst + "%" : "NA"})`
                                : "-"}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 14 }}>
                              {intra
                                ? `${convertToINR(tax.sgst_amount || 0)} (${tax.sgst != null ? tax.sgst + "%" : "NA"})`
                                : "-"}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 14 }}>
                              {inter
                                ? `${convertToINR(tax.igst_amount || 0)} (${tax.igst != null ? tax.igst + "%" : "NA"})`
                                : "-"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 14 }}>{it.remarks || "-"}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
          </Box>
        )}
      </Box>

      {selectedCreditNote && (
        <CreditNoteReview
          creditNote={selectedCreditNote}
          po_code={po_code}
          po_id={poData?.metadata?.po_id}
          handleClose={() => setSelectedCreditNote(null)}
        />
      )}
    </Box>
  );
};

export default InvoiceDetails;
